import { Skeleton } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { EVENTS, publish } from '@sendible/event-manager';
import { useInfiniteGetMessagesByRange } from '@sendible/frontendv2/src/models/calendar';
import { Content } from './index.styles';
import { EmptyStateText } from '../../index.styles';
import { CalendarCard } from '../../../CalendarCard';
import { DraggableCard } from '../../../CalendarGrid/components/DraggableCard';
import { useCalendarContext, useCalendarSidebarContext } from '../../../../context';
import { useSelectionContext } from '../../../../selectionContext';
import { fullDateToMonthString, getCalendarCardMedias } from '../../../../utils';
import { CreationButton } from '../../../CalendarGrid/components/CreationButton';

export const DaySidebarContent = () => {
  const user = useBridgeUser();
  const { changeSidebarMode, closeSidebar, todayDate } = useCalendarContext();
  const { sidebarDay } = useCalendarSidebarContext();
  const { t } = useTranslation('calendar');
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteGetMessagesByRange(sidebarDay, sidebarDay, true);
  const { clearVisibleMessageIds, setVisibleMessageIds } = useSelectionContext();

  const messages = useMemo(() => data?.pages.flatMap((page) => page) || [], [data]);
  const isPast = sidebarDay < todayDate;

  useEffect(() => {
    setVisibleMessageIds(
      'day-sidebar',
      messages.map((m) => m.id)
    );
  }, [messages, setVisibleMessageIds]);

  useEffect(() => () => clearVisibleMessageIds('day-sidebar'), [clearVisibleMessageIds]);

  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: '160px 0px 160px 0px',
  });

  const handleCreateNewMessageClick = () => {
    closeSidebar();
    publish(EVENTS.OPEN_COMPOSE, { mode: 'new', date: sidebarDay, offset: user.timezone });
  };

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    if (inView) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Content>
      {isLoading ? (
        <>
          <Skeleton
            height="4.875rem"
            width="100%"
          />
          <Skeleton
            height="4.875rem"
            width="100%"
          />
        </>
      ) : data && messages.length > 0 ? (
        <>
          {messages.map((m) => (
            <DraggableCard
              key={m.id}
              message={m}
              dayKey={sidebarDay}
            >
              <CalendarCard
                id={m.id}
                onClick={() => changeSidebarMode('message', fullDateToMonthString(m.sendDateLocalized), m.id)}
                deliveryStatus={m.deliveryStatus}
                postAt={m.sendDateLocalized}
                destinations={m.destinations || []}
                status={m.status}
                medias={getCalendarCardMedias(m.body)}
                recurrence={m.recurrence}
                title={m.body?.messageText || m.subject}
                type="preview"
                workspace={isWorkspaceMode ? m.author || m.workspace : m.workspace}
              />
            </DraggableCard>
          ))}
          {!isPast && (
            <CreationButton
              onClick={handleCreateNewMessageClick}
              view="weekly"
            />
          )}
          <div ref={sentinelRef} />
          {isFetchingNextPage && (
            <Skeleton
              height="4.875rem"
              width="100%"
            />
          )}
        </>
      ) : (
        <EmptyStateText data-testid="calendar-right-sidebar-empty-state">{t('no_posts_that_day')}</EmptyStateText>
      )}
    </Content>
  );
};
